import { useState, useEffect, useCallback } from "react";

interface PPJKData {
  nomorPpjk: string | null;
}

// Read once synchronously so consumers don't fire two requests (one
// with `nomorPpjk=null`, then a second after the useEffect resolves).
const readPpjkFromStorage = (): string | null => {
  if (typeof window === 'undefined') return null;
  const storedPpjk = localStorage.getItem('nomor_ppjk');
  const authData = localStorage.getItem('authData');

  let ppjkValue: string | null = storedPpjk;

  if ((!ppjkValue || ppjkValue.trim() === '') && authData) {
    try {
      const parsedAuth = JSON.parse(authData);
      const ppjkFromAuth = parsedAuth.ppjkNpwp || parsedAuth.nomorPpjk || null;
      if (ppjkFromAuth && ppjkFromAuth.trim() !== '') {
        ppjkValue = ppjkFromAuth;
      } else {
        ppjkValue = null;
      }
    } catch (error) {
      console.warn('Failed to parse authData:', error);
    }
  }

  if (ppjkValue && ppjkValue.trim() === '') {
    ppjkValue = null;
  }
  return ppjkValue;
};

export default function usePPJK() {
  // Lazy initializer hits localStorage on the first render so the value
  // is available synchronously — avoids the double fetch where a parent
  // hook depended on `nomorPpjk` and re-fired once useEffect populated it.
  const [nomorPpjk, setNomorPpjk] = useState<string | null>(() => readPpjkFromStorage());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Re-sync in case localStorage changed between mount and first effect
    // (e.g. impersonation switch). No-op when the value already matches.
    const next = readPpjkFromStorage();
    setNomorPpjk((prev) => (prev === next ? prev : next));
    setIsLoading(false);
  }, []);

  // Function to update PPJK data
  const updatePpjk = useCallback((newPpjk: string) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('nomor_ppjk', newPpjk);
      
      // Also update in authData for consistency
      const authData = localStorage.getItem('authData');
      if (authData) {
        try {
          const parsedAuth = JSON.parse(authData);
          parsedAuth.ppjkNpwp = newPpjk;
          localStorage.setItem('authData', JSON.stringify(parsedAuth));
        } catch (error) {
          console.warn('Failed to update authData with new PPJK:', error);
        }
      }
    }
    setNomorPpjk(newPpjk);
  }, []);

  // Function to get PPJK with fallback (should use user's private PPJK, not GATEWAY)
  const getPpjkWithFallback = useCallback((fallback?: string) => {
    return nomorPpjk || fallback || null;
  }, [nomorPpjk]);

  // Function to clear PPJK data
  const clearPpjk = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('nomor_ppjk');
    }
    setNomorPpjk(null);
  }, []);

  return {
    nomorPpjk,
    isLoading,
    updatePpjk,
    getPpjkWithFallback,
    clearPpjk
  };
}